import { useContext, useEffect, useState } from "react";
import {
  AlertComponent,
  NavBar,
  SearchInput,
  ShowContent,
  Spinner,
} from "../../../components";
import AuthContext from "../../../context/AuthContext";
import { useDate, useFetch, useRequest } from "../../../hooks";
import { WellProps } from "../../../interfaces/interfaces";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Grid,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import styles from "../../main.module.sass";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useFilter } from "../../../hooks/useFilter";
import {
  modernDataGridStyles,
  modernIconButtonStyles,
} from "../../../utils/dataGridStyles";

interface FetchResponse {
  success?: boolean;
  data: WellProps[];
}

interface ProposalProps {
  id: number;
  active: boolean;
  customName: string;
  date: string;
  idClient: number;
  idWell: number;
  path: string;
}

export const ProposalClient = () => {
  const theme = useTheme();
  const { handleRequest } = useRequest();
  const { getDateFromString } = useDate();
  const { clientId } = useContext(AuthContext);
  const { data, isLoading } = useFetch<FetchResponse>(
    `well-client/${clientId}`,
  );
  const matches = useMediaQuery("(min-width:600px)");
  const [dataProposal, setDataProposal] = useState<ProposalProps[]>([]);
  const [isError, setIsError] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [wellquery, setWellQuery] = useState("");
  const [pullQuery, setPullQuery] = useState("");

  const { filteredData: wellData } = useFilter<WellProps>(data?.data, {
    query: wellquery,
    field: "name",
  });

  const { filteredData: proposalData } = useFilter<ProposalProps>(
    dataProposal,
    {
      query: pullQuery,
      field: "customName",
    },
  );

  // Debug log for filtered data
  useEffect(() => {
    console.log("Filtered Proposal Data:", proposalData);
    console.log("Raw Proposal Data:", dataProposal);
    console.log("Pull Query:", pullQuery);
  }, [proposalData, dataProposal, pullQuery]);

  const fetchProposals = (idWell: number) => {
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest({ endpoint: `proposal-client/${idWell}`, options })
      .then((response) => {
        // Ensure each row has a unique id for DataGrid
        const dataWithIds = response.data.map(
          (item: ProposalProps, index: number) => ({
            ...item,
            id: item.id || index, // Use item.id if available, otherwise use index
          }),
        );
        setDataProposal(dataWithIds);
      })
      .catch((e) => setIsError(true));
  };

  const fetchProposalByKey = (key: string, name: string, download: boolean) => {
    if (key) {
      setIsCreatingReport(true);
      let options: RequestInit = {
        method: "POST",
        body: JSON.stringify({ key }),
        headers: {
          responseType: "Buffer",
        },
      };
      handleRequest({ endpoint: "pdf-proposal", options })
        .then((response) => {
          if (response.success) {
            createPdfFile(response.data, name, download);
          } else {
            setIsError(true);
          }
          setIsCreatingReport(false);
        })
        .catch((e) => setIsError(true));
    }
  };

  const createPdfFile = (
    data: { type: string; data: Buffer },
    name: string,
    download: boolean,
  ) => {
    try {
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(data.data).buffer], {
          type: "application/pdf;charset=utf-8",
        }),
      );
      if (download) {
        var link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
      } else {
        window.open(url);
      }
    } catch (e) {
      setIsError(true);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Well",
      renderHeader: () => <strong>{"WELL"}</strong>,
      sortable: true,
      width: 500,
    },
  ];

  const columnsProposal: GridColDef[] = [
    {
      field: "customName",
      headerName: "Proposal",
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.row.customName || ""}>
            <span>{params.row.customName || "N/A"}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "date",
      headerName: "Date",
      width: 150,
      sortable: true,
      renderCell: (params: any) => {
        return <span>{getDateFromString(params.row.date)}</span>;
      },
    },
    {
      field: "installationDate",
      headerName: "Installation Date",
      width: 150,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <span>
            {params.row.installationDate
              ? getDateFromString(params.row.installationDate)
              : "N/A"}
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        return (
          <div className="action-buttons">
            <Tooltip title="Open in a new Page">
              <IconButton
                sx={modernIconButtonStyles(theme)}
                size="small"
                onClick={() =>
                  fetchProposalByKey(
                    params.row.path,
                    params.row.customName,
                    false,
                  )
                }
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                sx={modernIconButtonStyles(theme)}
                size="small"
                onClick={() =>
                  fetchProposalByKey(
                    params.row.path,
                    params.row.customName,
                    true,
                  )
                }
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  let content: JSX.Element | JSX.Element[] = (
    <NavBar title="Proposals" buttons={[]} />
  );
  if (!wellData) {
    content = <Spinner />;
  } else {
    content = (
      <>
        <NavBar title="Proposals" buttons={[]} />
        <Grid container sx={{ marginTop: "2vh", marginLeft: "2vh" }}>
          <Grid item xs={12} md={6}>
            <SearchInput
              label="Search Well"
              value={wellquery}
              setValue={setWellQuery}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SearchInput
              label="Search Proposal"
              value={pullQuery}
              setValue={setPullQuery}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6} sx={{ marginTop: "2vh" }}>
            <DataGrid
              density={matches ? "standard" : "compact"}
              sx={{
                ...modernDataGridStyles(theme),
                width: "95%",
                maxWidth: "600px",
              }}
              rows={wellData}
              columns={columns}
              loading={isLoading}
              onCellClick={(params) => {
                fetchProposals(params.row.id);
              }}
              pagination
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6} sx={{ marginTop: "2vh" }}>
            <DataGrid
              density={matches ? "standard" : "compact"}
              sx={{
                ...modernDataGridStyles(theme),
                width: "95%",
                maxWidth: "600px",
                minHeight: 400,
              }}
              rows={proposalData}
              columns={columnsProposal}
              loading={isLoading}
              autoPageSize
              pagination
              getRowId={(row) => row.id}
              rowHeight={60}
              slots={{
                noRowsOverlay: () => (
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    <>Filter By Client and Well to see Proposals available</>
                  </div>
                ),
              }}
            />
          </Grid>
        </Grid>
        {isCreatingReport && <div className={styles.backdrop}></div>}
        {isError && <AlertComponent type="error" />}
      </>
    );
  }

  return <ShowContent error={""} isLoading={isLoading} content={content} />;
};
